import { Options, OuiMultiSelectDropdownComponent } from '@aiz-gl/omni-ui';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { type ControlValueAccessor, NgControl } from '@angular/forms';

@Component({
  selector: 'app-multi-select-dropdown',
  imports: [OuiMultiSelectDropdownComponent],
  templateUrl: './multi-select-dropdown.html',
  styleUrls: ['./multi-select-dropdown.css'],
})
export class MultiSelectDropdownComponent<T>
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  private readonly ALL_VALUE = '__all__';
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private mutationObserver?: MutationObserver;
  private removeDocumentClickListener?: () => void;
  private removeDocumentKeyupListener?: () => void;
  private removeWindowResizeListener?: () => void;

  public ngControl = inject(NgControl, {
    optional: true,
    self: true,
  });

  options = input<Options<T>>([]);
  enableAll = input<boolean>(false);

  optionsWithAll = computed(() => {
    const options = this.options() || [];
    if (!this.enableAll() || options.length === 0) {
      return options;
    }

    const hasAllOption = options.some(
      (option) => String(option.value) === this.ALL_VALUE,
    );

    if (hasAllOption) {
      return options;
    }

    return [{ label: 'All', value: this.ALL_VALUE as T }, ...options];
  });

  value: T[] = [];
  isDisabled = false;

  constructor() {
    if (this.ngControl) {
      // Manual assignment is required when NOT using the providers array
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      if (!this.enableAll() || !this.value.includes(this.ALL_VALUE as T)) {
        return;
      }

      const allValues = this.getAllOptionValues();
      const nextValue = [this.ALL_VALUE as T, ...allValues] as T[];

      if (allValues.length === 0 || this.hasSameValues(this.value, nextValue)) {
        return;
      }

      queueMicrotask(() => {
        if (this.hasSameValues(this.value, nextValue)) {
          return;
        }

        this.value = nextValue;
      });
    });

  }

  ngAfterViewInit(): void {
    const dropdown = this.getDropdownElement();
    if (!dropdown) {
      return;
    }

    this.mutationObserver = new MutationObserver(() => {
      this.schedulePlacementCheck();
    });

    this.mutationObserver.observe(dropdown, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    this.removeDocumentClickListener = this.renderer.listen(
      'document',
      'click',
      () => this.schedulePlacementCheck(),
    );

    this.removeDocumentKeyupListener = this.renderer.listen(
      'document',
      'keyup',
      () => this.schedulePlacementCheck(),
    );

    this.removeWindowResizeListener = this.renderer.listen('window', 'resize', () => {
      this.schedulePlacementCheck();
    });
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.removeDocumentClickListener?.();
    this.removeDocumentKeyupListener?.();
    this.removeWindowResizeListener?.();
  }

  // Getter for cleaner template logic
  get isInvalid(): boolean {
    return !!(
      this.ngControl &&
      this.ngControl.invalid &&
      (this.ngControl.touched || this.ngControl.dirty)
    );
  }

  // CVA Implementation Methods
  onChange: (value: T[]) => void = () => {
    // Placeholder for the callback registered by Angular
  };
  onTouched: () => void = () => {
    // Placeholder for the callback registered by Angular
  };

  writeValue(value: T[]): void {
    const incoming = value || [];

    if (!this.enableAll()) {
      if (!this.hasSameValues(this.value, incoming)) {
        this.value = incoming;
      }
      return;
    }

    const allValues = this.getAllOptionValues();
    const includesAllOption = incoming.some(
      (item) => String(item) === this.ALL_VALUE,
    );

    const selectedWithoutAll = incoming.filter(
      (item) => String(item) !== this.ALL_VALUE,
    );

    const hasAllSelected =
      allValues.length > 0 &&
      selectedWithoutAll.length === allValues.length &&
      allValues.every((option) => selectedWithoutAll.includes(option));

    const nextValue =
      includesAllOption || hasAllSelected
        ? ([this.ALL_VALUE as T, ...allValues] as T[])
        : selectedWithoutAll;

    if (!this.hasSameValues(this.value, nextValue)) {
      this.value = nextValue;
    }
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onDropdownUpdate(selectedItems: T[]): void {
    if (!this.enableAll()) {
      if (!this.hasSameValues(this.value, selectedItems)) {
        this.value = selectedItems;
        this.onChange(selectedItems);
      }
      this.onTouched();
      return;
    }

    const allOption = this.ALL_VALUE as T;
    const allValues = this.getAllOptionValues();

    const hadAll = this.value.some((item) => String(item) === this.ALL_VALUE);
    const hasAllNow = selectedItems.some(
      (item) => String(item) === this.ALL_VALUE,
    );
    const selectedWithoutAll = selectedItems.filter(
      (item) => String(item) !== this.ALL_VALUE,
    );

    let nextValue: T[] = selectedWithoutAll;

    // Scenario 1: selecting All selects every option.
    if (hasAllNow && !hadAll) {
      nextValue = [allOption, ...allValues];
    }

    // Scenario 2: unchecking All clears all options.
    // If All was removed but all individual options remain selected,
    // it means user unchecked only the All option.
    if (!hasAllNow && hadAll && selectedWithoutAll.length === allValues.length) {
      nextValue = [];
    }

    // Scenario 3: if any individual option is unchecked,
    // keep selected items but ensure All is unchecked.
    if (hadAll && selectedWithoutAll.length < allValues.length) {
      nextValue = selectedWithoutAll;
    }

    if (!this.hasSameValues(this.value, nextValue)) {
      this.value = nextValue;
      this.onChange(nextValue);
    }
    this.onTouched(); // Important for marking control as 'touched'
  }

  private getAllOptionValues(): T[] {
    return (this.options() || []).map((option) => option.value as T);
  }

  private hasSameValues(left: T[], right: T[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((value, index) => value === right[index]);
  }

  private schedulePlacementCheck(): void {
    requestAnimationFrame(() => this.applyDropdownPlacement());
  }

  private applyDropdownPlacement(): void {
    const dropdown = this.getDropdownElement();
    if (!dropdown) {
      this.renderer.removeClass(this.hostElement.nativeElement, 'open-up');
      return;
    }

    const panel = this.getDropdownPanelElement(dropdown);
    if (!panel) {
      this.renderer.removeClass(this.hostElement.nativeElement, 'open-up');
      return;
    }

    const dropdownRect = dropdown.getBoundingClientRect();
    const containerRect = this.getClippingContainerRect(dropdown);
    const maxPanelHeight = this.getMaxPanelHeight(panel);
    const requestedPanelHeight = panel.scrollHeight || maxPanelHeight;
    const panelHeight = Math.min(maxPanelHeight, requestedPanelHeight);
    const minimumUsableHeight = Math.min(220, panelHeight);

    const gap = 4;
    const spaceBelow = Math.max(0, containerRect.bottom - dropdownRect.bottom - gap);
    const spaceAbove = Math.max(0, dropdownRect.top - containerRect.top - gap);
    const shouldOpenUp =
      spaceBelow < minimumUsableHeight &&
      spaceAbove > spaceBelow;

    if (shouldOpenUp) {
      this.renderer.addClass(this.hostElement.nativeElement, 'open-up');
      return;
    }

    this.renderer.removeClass(this.hostElement.nativeElement, 'open-up');
  }

  private getDropdownElement(): HTMLElement | null {
    return this.hostElement.nativeElement.querySelector('oui-multi-select-dropdown');
  }

  private getDropdownPanelElement(dropdown: HTMLElement): HTMLElement | null {
    return (
      dropdown.querySelector('[role="listbox"]') ||
      dropdown.querySelector('.dropdown-menu') ||
      dropdown.querySelector('.dropdown-panel') ||
      dropdown.querySelector('.options-container') ||
      dropdown.querySelector('.options-list') ||
      dropdown.querySelector('.menu')
    ) as HTMLElement | null;
  }

  private getClippingContainerRect(element: HTMLElement): DOMRect {
    const container = this.getClosestClippingContainer(element);

    if (!container) {
      return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    }

    return container.getBoundingClientRect();
  }

  private getClosestClippingContainer(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element.parentElement;

    while (current) {
      const styles = getComputedStyle(current);
      const overflowY = styles.overflowY;
      const overflow = styles.overflow;
      const canClip = /(auto|scroll|hidden|clip)/.test(`${overflowY} ${overflow}`);

      if (canClip) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private getMaxPanelHeight(panel: HTMLElement): number {
    const maxHeight = getComputedStyle(panel).maxHeight;
    const parsed = Number.parseFloat(maxHeight);

    if (!Number.isFinite(parsed)) {
      return 400;
    }

    return parsed;
  }
}
